import { scopedApi, type ApiScope } from "@/features/utils/api-scope";
import { api } from "@/features/utils/fetch-server";
import type {
  CreateCustomerRequest,
  Customer,
  CustomerSummary,
  GetCustomerResponse,
  ListCustomersResponse,
  UpdateCustomerRequest,
} from "../types";

export class CustomersAPI {
  // List all customers
  static listCustomers = async (
    clientId: string,
    params?: {
      search?: string;
      limit?: number;
      offset?: number;
      sort_by?: string;
      sort_order?: "asc" | "desc";
      hide_unknown?: boolean;
    },
    // `staff`: the client's file in the platform back-office (read only).
    scope: ApiScope = "tenant",
  ): Promise<ListCustomersResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append("search", params.search);
    if (params?.limit !== undefined)
      searchParams.append("limit", params.limit.toString());
    if (params?.offset !== undefined)
      searchParams.append("offset", params.offset.toString());
    if (params?.sort_by) searchParams.append("sort_by", params.sort_by);
    if (params?.sort_order)
      searchParams.append("sort_order", params.sort_order);
    if (params?.hide_unknown) searchParams.append("hide_unknown", "true");

    const queryString = searchParams.toString();
    const url = `${scopedApi(scope, "customers", clientId)}/customers/${queryString ? `?${queryString}` : ""}`;

    return api.get<ListCustomersResponse>(url);
  };

  // Get a single customer by ID
  static getCustomer = async (
    clientId: string,
    customerId: string,
    scope: ApiScope = "tenant",
  ): Promise<GetCustomerResponse> => {
    return api.get<GetCustomerResponse>(
      `${scopedApi(scope, "customers", clientId)}/customers/${customerId}`,
    );
  };

  // Several customers at once, as a list shows them — at most 100 ids.
  static getCustomerSummaries = async (
    clientId: string,
    customerIds: string[],
    scope: ApiScope = "tenant",
  ): Promise<{ customers: CustomerSummary[] }> => {
    const ids = encodeURIComponent(customerIds.join(","));
    return api.get<{ customers: CustomerSummary[] }>(
      `${scopedApi(scope, "customers", clientId)}/customers/batch?ids=${ids}`,
    );
  };

  // Create a new customer
  static createCustomer = async (
    clientId: string,
    data: CreateCustomerRequest,
  ): Promise<{ customer: Customer }> => {
    return api.post<{ customer: Customer }, CreateCustomerRequest>(
      `/api/customers/v1/${clientId}/customers/`,
      data,
    );
  };

  // Update a customer
  static updateCustomer = async (
    clientId: string,
    customerId: string,
    data: UpdateCustomerRequest,
  ): Promise<{ customer: Customer }> => {
    return api.put<{ customer: Customer }, UpdateCustomerRequest>(
      `/api/customers/v1/${clientId}/customers/${customerId}`,
      data,
    );
  };

  // Delete a customer
  static deleteCustomer = async (
    clientId: string,
    customerId: string,
  ): Promise<{ message: string }> => {
    return api.delete<{ message: string }>(
      `/api/customers/v1/${clientId}/customers/${customerId}`,
    );
  };
}
